import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangleIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/Modal';
import Button from '@/components/form/Button';
import Input from '@/components/form/Input';
import { useAuth } from '@/context/AuthContext';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DeleteAccountModal({ isOpen, onClose }: DeleteAccountModalProps) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleDelete = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setLoading(true);

      // First verify the password
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error('No user email found');

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password
      });

      if (signInError) {
        toast.error('Incorrect password');
        return;
      }

      // Delete all user data
      const { error: deleteError } = await supabase.rpc('delete_user_data');
      if (deleteError) throw deleteError;

      // Sign out the user
      await signOut();
      
      toast.success('Account deleted successfully');
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Account"
    >
      <div className="space-y-6">
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex items-start">
            <AlertTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Warning</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>This action cannot be undone. This will permanently:</p>
                <ul className="list-disc list-inside mt-2">
                  <li>Delete your account</li>
                  <li>Delete all your SNAPs</li>
                  <li>Remove all your votes and comments</li>
                  <li>Delete your profile information</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Please enter your password to confirm account deletion:
          </p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDelete}
            loading={loading}
            className="!bg-red-600 hover:!bg-red-700"
          >
            Delete Account
          </Button>
        </div>
      </div>
    </Modal>
  );
}